import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Button, Cards, ContentLayout, Header,
    LineChart, Spinner, SpaceBetween, StatusIndicator,
} from '@cloudscape-design/components';
import { useNavigate, useParams } from 'react-router-dom';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { alphaAnalysisApi } from '../../client/clients';
import { AlphaResult } from '../../api';
import { useArticlePopup, InlineSpinner } from './ArticlePopupModal';
import { GetArticleDetailsCredentialKeyEnum } from '../../api';
import { PublisherMetricsMiniChart } from './PublisherMetricsMiniChart';
import { usePublisherMetrics } from './usePublisherMetrics';
import { buildSectorOutlookSeries } from './sectorForecastUtils';

function stanceColor(s: number): 'success' | 'warning' | 'error' {
    return s >= 20 ? 'success' : s >= -20 ? 'warning' : 'error';
}

export const AlphaAnalystPage: React.FC = () => {
    const { authorId } = useParams<{ authorId: string }>();
    const navigate = useNavigate();
    const [analyses, setAnalyses] = useState<AlphaResult[]>([]);
    const [days, setDays] = useState(365);
    const [loading, setLoading] = useState(true);
    const { openArticle, loadingArticleId, modalElement } = useArticlePopup();
    const authorName = analyses[0]?.authorName ?? authorId;
    useDocumentTitle(`Analyst — ${authorName}`);

    useEffect(() => {
        setLoading(true);
        alphaAnalysisApi.getAnalystResults(authorId!, days)
            .then(r => setAnalyses(r.data))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [authorId, days]);

    // Article-level outlook line — falls back to sectorOutlook when the
    // per-ticker tickers[] field is empty (production data shape).
    const outlookSeries = useMemo(() => {
        const points = buildSectorOutlookSeries(analyses);
        return [{
            title: 'Daily',
            type: 'line' as const,
            data: points.map(p => ({ x: new Date(p.date), y: p.value })),
        }];
    }, [analyses]);

    const authorIdList = useMemo(() => (authorId ? [authorId] : []), [authorId]);
    const { metricsByAuthor } = usePublisherMetrics(authorIdList);
    const authorMetrics = authorId ? metricsByAuthor[authorId] : undefined;

    const xStart = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    const xEnd   = new Date();

    if (loading) return <Box textAlign="center" padding="xxl"><Spinner size="large" /></Box>;

    return (
        <ContentLayout
            header={
                <Header
                    variant="h1"
                    actions={
                        <SpaceBetween direction="horizontal" size="xs">
                            {[30, 90, 180, 365].map(d => (
                                <Button
                                    key={d}
                                    variant={days === d ? 'primary' : 'normal'}
                                    onClick={() => setDays(d)}
                                >
                                    {d}d
                                </Button>
                            ))}
                        </SpaceBetween>
                    }
                >
                    Analyst: {authorName}
                </Header>
            }
        >
            <SpaceBetween size="l">
                {authorMetrics && (
                    <Box>
                        <Box variant="awsui-key-label" margin={{ bottom: 'xs' }}>
                            Earner score — avg stance-weighted % return per timeframe
                        </Box>
                        <PublisherMetricsMiniChart
                            metrics={authorMetrics}
                            authorName={typeof authorName === 'string' ? authorName : undefined}
                        />
                    </Box>
                )}
                <LineChart
                    series={outlookSeries}
                    xDomain={[xStart, xEnd]}
                    yDomain={[-100, 100]}
                    xTitle="Date"
                    yTitle="Article outlook"
                    height={300}
                    empty={<Box textAlign="center">No analyses for this analyst in the selected window</Box>}
                    noMatch={<Box textAlign="center">No matching data</Box>}
                />

                <Cards
                    items={analyses}
                    cardDefinition={{
                        header: (item: AlphaResult) => {
                            const artId    = item.articleId ?? '';
                            const isLoading = loadingArticleId === artId;
                            return (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    <button
                                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#0972d3', textAlign: 'left', fontSize: 'inherit', fontWeight: 'inherit' }}
                                        onClick={() => artId && openArticle(artId, GetArticleDetailsCredentialKeyEnum.SeekingAlphaProCookie)}
                                        disabled={isLoading || !artId}
                                    >
                                        {item.articleTitle ?? ''}
                                    </button>
                                    <InlineSpinner show={isLoading} />
                                </span>
                            );
                        },
                        sections: [
                            {
                                id: 'date',
                                header: 'Published',
                                content: (item: AlphaResult) => (item.publishedAt ?? '').substring(0, 10),
                            },
                            {
                                id: 'sector',
                                header: 'Sector',
                                content: (item: AlphaResult) => (
                                    <span
                                        style={{ cursor: 'pointer', color: '#0972d3' }}
                                        onClick={() => navigate(`/alpha/sectors/${encodeURIComponent((item.sector ?? '').replace(/ \/ /g, '--'))}`)}
                                    >
                                        {item.sector}
                                    </span>
                                ),
                            },
                            {
                                id: 'tickers',
                                header: 'Tickers',
                                content: (item: AlphaResult) => (
                                    <SpaceBetween direction="horizontal" size="xs">
                                        {(item.tickers ?? []).map(t => (
                                            <StatusIndicator key={t.ticker} type={stanceColor(t.stance ?? 0)}>
                                                <span
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => navigate(`/alpha/stocks/${t.ticker}`)}
                                                >
                                                    {t.ticker} {(t.stance ?? 0) > 0 ? '+' : ''}{t.stance ?? 0}
                                                </span>
                                            </StatusIndicator>
                                        ))}
                                    </SpaceBetween>
                                ),
                            },
                        ],
                    }}
                    trackBy="articleId"
                    empty={<Box textAlign="center">No analyses found</Box>}
                />
                {modalElement}
            </SpaceBetween>
        </ContentLayout>
    );
};
